import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { SplitterOrientation } from './splitter.types';

/**
 * Draggable handle component for HxSplitter Positioned between panels to allow user-controlled resizing
 *
 * @example
 *   ```html
 *   <hx-splitter-handle
 *     [orientation]="'vertical'"
 *     [ariaValueNow]="50"
 *     (handleMouseDown)="onDragStart($event)">
 *   </hx-splitter-handle>
 *   ```;
 */
@Component({
  selector: 'hx-splitter-handle',
  standalone: true,
  template: `
    <div
      class="hx-splitter-handle"
      [class.hx-splitter-handle--focused]="isFocused()"
      [class.hx-splitter-handle--disabled]="disabled()"
      [attr.role]="'separator'"
      [attr.aria-orientation]="orientation()"
      [attr.aria-valuenow]="ariaValueNow()"
      [attr.aria-valuemin]="ariaValueMin()"
      [attr.aria-valuemax]="ariaValueMax()"
      [attr.aria-label]="ariaLabel()"
      [attr.tabindex]="disabled() ? -1 : 0"
      [style.cursor]="cursorStyle()"
      (mousedown)="onMouseDown($event)"
      (touchstart)="onTouchStart($event)"
      (keydown)="onKeyDown($event)"
      (focus)="onFocus()"
      (blur)="onBlur()">
      <div class="hx-splitter-handle__grip"></div>
    </div>
  `,
  styleUrls: ['./splitter-handle.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.hx-splitter-handle-container]': 'true',
    '[class.hx-splitter-handle-container--vertical]': 'orientation() === "vertical"',
    '[class.hx-splitter-handle-container--horizontal]': 'orientation() === "horizontal"',
    '[style.width]': 'orientation() === "vertical" ? handleSize() : "100%"',
    '[style.height]': 'orientation() === "horizontal" ? handleSize() : "100%"',
  },
})
export class HxSplitterHandle {
  /** Handle orientation (affects cursor and appearance) */
  readonly orientation = input<SplitterOrientation>('vertical');

  /** Disable handle interactions */
  readonly disabled = input<boolean>(false);

  /** Accessible label for the handle */
  readonly ariaLabel = input<string>('Resize panels');

  /** Current panel percentage for screen readers */
  readonly ariaValueNow = input<number>(50);

  /** Minimum panel percentage */
  readonly ariaValueMin = input<number>(0);

  /** Maximum panel percentage */
  readonly ariaValueMax = input<number>(100);

  /** Handle size (width for vertical, height for horizontal) */
  readonly handleSize = input<string>('8px');

  /** Emitted when user presses mouse on handle */
  readonly handleMouseDown = output<MouseEvent>();

  /** Emitted when user touches handle */
  readonly handleTouchStart = output<TouchEvent>();

  /** Emitted when user presses key while handle focused */
  readonly handleKeyDown = output<KeyboardEvent>();

  /** Internal state: whether handle currently has keyboard focus */
  protected readonly isFocused = signal<boolean>(false);

  /** Computed cursor style based on orientation */
  protected readonly cursorStyle = computed<string>(() => {
    if (this.disabled()) {
      return 'not-allowed';
    }
    return this.orientation() === 'vertical' ? 'col-resize' : 'row-resize';
  });

  /** Handle mouse down event */
  protected onMouseDown(event: MouseEvent): void {
    if (this.disabled()) {
      return;
    }
    this.handleMouseDown.emit(event);
  }

  /** Handle touch start event */
  protected onTouchStart(event: TouchEvent): void {
    if (this.disabled()) {
      return;
    }
    this.handleTouchStart.emit(event);
  }

  /** Handle keyboard event */
  protected onKeyDown(event: KeyboardEvent): void {
    if (this.disabled()) {
      return;
    }
    this.handleKeyDown.emit(event);
  }

  /** Handle focus event */
  protected onFocus(): void {
    this.isFocused.set(true);
  }

  /** Handle blur event */
  protected onBlur(): void {
    this.isFocused.set(false);
  }
}
